import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Panel } from "./panel";
import { LossDevelopmentChart } from "./charts/loss-development-chart";
import { Skeleton } from "../../ui/skeleton";
import { Button } from "../../ui/button";
import { PAGE_SIZE, Pager } from "./pager";
import {
  ALL_INDUSTRIES_LABEL,
  ULTIMATE_AGE,
  useDevelopmentLedger,
  useLossDevelopment,
} from "../../pages/PortfolioPerformance/lib/api/loss-calibration";
import type { DevelopmentFactor, LossDevelopmentLedgerRow } from "../../pages/PortfolioPerformance/lib/api/loss-calibration";
import { useBorrowerIndustries } from "../../pages/PortfolioPerformance/lib/api/borrower-industries";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { BasisToggle, type ThreeWayBasis } from "./basis-toggle";
import { cn } from "../../utils/common/cn";

const SCOPE_CAPTION =
  "Built by industry for now. Term materially affects development speed, so industry-by-term is a planned refinement, not the final design.";

/** Sentinel for the ledger's year filter, matching ALL_INDUSTRIES_LABEL's role
 *  next to it. A Radix Select cannot hold an empty-string value, so "no filter"
 *  needs a real one. */
const ALL_YEARS_LABEL = "All Years";

function ageLabel(age: number) {
  return `${age}m`;
}

function FactorsTable({ factors }: { factors: DevelopmentFactor[] }) {
  return (
    <div className="mt-6 overflow-x-auto border border-border bg-card">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            {factors.map((factor) => (
              <th
                key={`${factor.fromAge}-${factor.toAge}`}
                className="px-3 py-2.5 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
              >
                {ageLabel(factor.fromAge)} → {ageLabel(factor.toAge)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {factors.map((factor) => (
              <td
                key={`${factor.fromAge}-${factor.toAge}`}
                className="px-3 py-2.5 text-center font-semibold tabular-nums text-foreground"
              >
                {factor.factor.toFixed(3)}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

type LedgerSortKey = "vintage" | "industry" | "projectedUltimateLossPct" | "actualUltimateLossPct" | "errorPct";

const LEDGER_COLUMNS: Array<{ key: LedgerSortKey; label: string; align: "left" | "right" }> = [
  { key: "vintage", label: "Vintage", align: "left" },
  { key: "industry", label: "Industry", align: "left" },
  { key: "projectedUltimateLossPct", label: "Projected Loss", align: "right" },
  { key: "actualUltimateLossPct", label: "Actual Loss", align: "right" },
  { key: "errorPct", label: "Error", align: "right" },
];

interface VintageGroup {
  vintage: number;
  industry: string;
  ultimate: LossDevelopmentLedgerRow;
  ages: LossDevelopmentLedgerRow[];
}

function groupByVintage(rows: LossDevelopmentLedgerRow[]): VintageGroup[] {
  const byVintage = new Map<number, LossDevelopmentLedgerRow[]>();
  for (const row of rows) {
    const group = byVintage.get(row.vintage);
    if (group) group.push(row);
    else byVintage.set(row.vintage, [row]);
  }
  return Array.from(byVintage.entries()).map(([vintage, ages]) => {
    const sortedAges = [...ages].sort((a, b) => a.ageAtProjectionMonths - b.ageAtProjectionMonths);
    const ultimate =
      sortedAges.find((row) => row.ageAtProjectionMonths === ULTIMATE_AGE) ??
      sortedAges[sortedAges.length - 1];
    return { vintage, industry: ultimate.industry, ultimate, ages: sortedAges };
  });
}

function AgeBreakdownTable({ ages }: { ages: LossDevelopmentLedgerRow[] }) {
  return (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr className="border-b border-border/60">
          <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Age At Projection
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Projected
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Actual
          </th>
          <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Error
          </th>
        </tr>
      </thead>
      <tbody>
        {ages.map((row) => (
          <tr key={row.ageAtProjectionMonths} className="border-b border-border/40 last:border-b-0">
            <td className="px-3 py-2 tabular-nums text-foreground">
              {ageLabel(row.ageAtProjectionMonths)}
              {row.ageAtProjectionMonths === ULTIMATE_AGE ? (
                <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                  Ultimate
                </span>
              ) : null}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.projectedUltimateLossPct.toFixed(2)}%
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.actualUltimateLossPct == null ? (
                <span className="text-muted-foreground">Pending</span>
              ) : (
                `${row.actualUltimateLossPct.toFixed(2)}%`
              )}
            </td>
            <td className="px-3 py-2 text-right tabular-nums">
              {row.errorPct == null ? (
                <span className="text-muted-foreground">Pending</span>
              ) : (
                `${row.errorPct > 0 ? "+" : ""}${row.errorPct.toFixed(2)}%`
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LedgerTable({ rows }: { rows: LossDevelopmentLedgerRow[] }) {
  const [sortKey, setSortKey] = useState<LedgerSortKey>("vintage");
  const [descending, setDescending] = useState(true);
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [offset, setOffset] = useState(0);

  const groups = useMemo(() => groupByVintage(rows), [rows]);

  const sorted = useMemo(() => {
    const copy = [...groups];
    copy.sort((a, b) => {
      const av = sortKey === "vintage" || sortKey === "industry" ? a[sortKey] : a.ultimate[sortKey];
      const bv = sortKey === "vintage" || sortKey === "industry" ? b[sortKey] : b.ultimate[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv));
    });
    return descending ? copy.reverse() : copy;
  }, [groups, sortKey, descending]);

  // Client-side paging (the ledger is fetched whole, then filtered/sorted here).
  // Clamp so a shrinking filter result never leaves us on an empty page.
  const pageOffset = Math.min(offset, Math.max(0, Math.floor((sorted.length - 1) / PAGE_SIZE) * PAGE_SIZE));
  const pageRows = sorted.slice(pageOffset, pageOffset + PAGE_SIZE);

  return (
    <>
    <div className="overflow-x-auto border border-border bg-card">
      <table className="w-full min-w-[900px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-border bg-secondary/50">
            <th className="w-8 px-2 py-2.5" />
            {LEDGER_COLUMNS.map((column) => (
              <th
                key={column.key}
                className={cn(
                  "px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground",
                  column.align === "right" ? "text-right" : "text-left",
                )}
              >
                <button
                  type="button"
                  className="uppercase tracking-wider hover:text-foreground"
                  onClick={() => {
                    setOffset(0);
                    if (sortKey === column.key) setDescending((prev) => !prev);
                    else {
                      setSortKey(column.key);
                      setDescending(true);
                    }
                  }}
                >
                  {column.label}
                  {sortKey === column.key ? (descending ? " ↓" : " ↑") : ""}
                </button>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {pageRows.map((group) => (
            <Fragment key={group.vintage}>
              <tr className="border-b border-border/60 last:border-b-0">
                <td className="px-2 py-2.5">
                  <button
                    type="button"
                    className="flex size-5 items-center justify-center border border-border text-muted-foreground hover:text-foreground"
                    aria-label={expanded[group.vintage] ? "Collapse term structure" : "Expand term structure"}
                    onClick={() =>
                      setExpanded((e) => ({ ...e, [group.vintage]: !e[group.vintage] }))
                    }
                  >
                    {expanded[group.vintage] ? (
                      <ChevronDown className="size-3" />
                    ) : (
                      <ChevronRight className="size-3" />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2.5 font-medium tabular-nums text-foreground">
                  {group.vintage}
                </td>
                <td className="px-3 py-2.5">{group.industry}</td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.projectedUltimateLossPct.toFixed(2)}%
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.actualUltimateLossPct == null ? (
                    <span className="text-muted-foreground">Pending</span>
                  ) : (
                    `${group.ultimate.actualUltimateLossPct.toFixed(2)}%`
                  )}
                </td>
                <td className="px-3 py-2.5 text-right tabular-nums">
                  {group.ultimate.errorPct == null ? (
                    <span className="text-muted-foreground">Pending</span>
                  ) : (
                    `${group.ultimate.errorPct > 0 ? "+" : ""}${group.ultimate.errorPct.toFixed(2)}%`
                  )}
                </td>
              </tr>
              {expanded[group.vintage] ? (
                <tr className="border-b border-border/60 last:border-b-0 bg-secondary/20">
                  <td />
                  <td colSpan={LEDGER_COLUMNS.length} className="px-3 pb-3 pt-1">
                    <AgeBreakdownTable ages={group.ages} />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
    <Pager offset={pageOffset} total={sorted.length} onChange={setOffset} />
    </>
  );
}

function VintageToggle({
  vintages,
  selected,
  onToggle,
}: {
  vintages: number[];
  selected: number[];
  onToggle: (vintage: number) => void;
}) {
  const active = selected.length ? selected : vintages;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Funded year
      </span>
      {vintages.map((vintage) => (
        <button
          key={vintage}
          type="button"
          onClick={() => onToggle(vintage)}
          aria-pressed={active.includes(vintage)}
          className={cn(
            "border px-2 py-1 text-xs font-medium tabular-nums transition-colors",
            active.includes(vintage)
              ? "border-primary bg-primary/15 text-foreground"
              : "border-border text-muted-foreground hover:text-foreground",
          )}
        >
          {vintage}
        </button>
      ))}
    </div>
  );
}

function IndustryMultiSelect({
  industries,
  selected,
  onChange,
}: {
  industries: string[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const label =
    selected.length === 0
      ? ALL_INDUSTRIES_LABEL
      : selected.length === 1
        ? selected[0]!
        : `${selected.length} Industries`;

  function toggle(industry: string) {
    onChange(
      selected.includes(industry)
        ? selected.filter((i) => i !== industry)
        : [...selected, industry],
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label="Industry"
          className="h-9 w-[220px] justify-between gap-2 text-sm font-normal"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-[320px] w-[220px] overflow-y-auto">
        <DropdownMenuCheckboxItem
          checked={selected.length === 0}
          onSelect={(e) => e.preventDefault()}
          onCheckedChange={() => onChange([])}
        >
          {ALL_INDUSTRIES_LABEL}
        </DropdownMenuCheckboxItem>
        <DropdownMenuSeparator />
        {industries.map((industry) => (
          <DropdownMenuCheckboxItem
            key={industry}
            checked={selected.includes(industry)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={() => toggle(industry)}
          >
            {industry}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function LossDevelopmentSection() {
  const industriesQuery = useBorrowerIndustries();
  const industries = industriesQuery.data ?? [];
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [selectedVintages, setSelectedVintages] = useState<number[]>([]);
  const [ledgerYear, setLedgerYear] = useState<string>(ALL_YEARS_LABEL);
  // The toggle picks the rows of the tables behind both sections (realized_tf / cohort_active_tf); within them `consider_XXmo` decides
  // per age whether a point is the actual reading (solid) or the projection (dotted), and an Error of "Pending" in the ledger.
  const [basis, setBasis] = useState<ThreeWayBasis>("realized");

  const developmentQuery = useLossDevelopment(selectedIndustries, basis);
  const ledgerQuery = useDevelopmentLedger(selectedIndustries, basis);

  const points = useMemo(() => developmentQuery.data?.points ?? [], [developmentQuery.data]);
  const vintages = useMemo(
    () => Array.from(new Set(points.map((p) => p.vintage))).sort((a, b) => a - b),
    [points],
  );
  const activeSelectedVintages = selectedVintages.filter((v) => vintages.includes(v));
  const visiblePoints = useMemo(
    () =>
      activeSelectedVintages.length ? points.filter((p) => activeSelectedVintages.includes(p.vintage)) : points,
    [points, activeSelectedVintages],
  );

  const basisLedgerRows = useMemo(() => ledgerQuery.data ?? [], [ledgerQuery.data]);

  const ledgerYears = useMemo(
    () => Array.from(new Set(basisLedgerRows.map((row) => row.vintage))).sort((a, b) => b - a),
    [basisLedgerRows],
  );

  const activeLedgerYear = ledgerYears.some((year) => String(year) === ledgerYear)
    ? ledgerYear
    : ALL_YEARS_LABEL;

  const ledgerRows = useMemo(() => {
    if (activeLedgerYear === ALL_YEARS_LABEL) return basisLedgerRows;
    return basisLedgerRows.filter((row) => String(row.vintage) === activeLedgerYear);
  }, [basisLedgerRows, activeLedgerYear]);

  function toggleVintage(vintage: number) {
    setSelectedVintages((previous) =>
      previous.includes(vintage)
        ? previous.filter((v) => v !== vintage)
        : [...previous, vintage],
    );
  }

  const industrySelect = (
    <IndustryMultiSelect
      industries={industries}
      selected={selectedIndustries}
      onChange={setSelectedIndustries}
    />
  );

  const basisToggle = (
    <BasisToggle
      value={basis}
      onChange={setBasis}
      realizedHint="Contracts that have matured"
      projectedHint="Contracts still running"
      activeHint="Funded years that still have active contracts"
    />
  );

  const ledgerControls = (
    <div className="flex flex-wrap items-center gap-2">
      {basisToggle}
      <Select value={activeLedgerYear} onValueChange={setLedgerYear}>
        <SelectTrigger className="w-[130px]" aria-label="Funded year">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_YEARS_LABEL}>{ALL_YEARS_LABEL}</SelectItem>
          {ledgerYears.map((year) => (
            <SelectItem key={year} value={String(year)}>
              {year}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {industrySelect}
    </div>
  );

  return (
    <>
      <Panel
        className="mt-6"
        title="Loss Development"
        description="Cumulative loss by vintage against age, actual solid and projected dotted."
        right={
          <div className="flex flex-wrap items-center gap-2">
            {basisToggle}
            {industrySelect}
          </div>
        }
      >
        <p className="-mt-2 mb-4 text-xs text-muted-foreground">{SCOPE_CAPTION}</p>

        {developmentQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load loss development for{" "}
            {selectedIndustries.length ? selectedIndustries.join(", ") : ALL_INDUSTRIES_LABEL}.
          </div>
        ) : developmentQuery.isLoading || !developmentQuery.data ? (
          <Skeleton className="h-[340px] w-full" />
        ) : (
          <>
            <VintageToggle
              vintages={vintages}
              selected={activeSelectedVintages}
              onToggle={toggleVintage}
            />
            {vintages.length === 0 ? (
              <p className="text-sm text-muted-foreground">No funded years under this toggle for this ticket size yet.</p>
            ) : (
              <LossDevelopmentChart points={visiblePoints} allVintages={vintages} showProjected />
            )}
            <p className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Age-to-age development factors
            </p>
            <FactorsTable factors={developmentQuery.data.factors} />
            <p className="mt-2 text-xs text-muted-foreground">
              Projected development multiples for the most recent funded year.
            </p>
          </>
        )}
      </Panel>

      <Panel
        className="mt-6"
        title="Validation Ledger"
        description="Every projection run ever made. Rows are appended, never replaced or deleted."
        right={ledgerControls}
      >
        {ledgerQuery.isError ? (
          <div
            role="alert"
            className="border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          >
            Could not load the validation ledger.
          </div>
        ) : ledgerQuery.isLoading || !ledgerQuery.data ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : (
          <LedgerTable rows={ledgerRows} />
        )}
      </Panel>
    </>
  );
}
